import Seo from "@/components/Seo";
import { useState } from "react";
import api from "@/lib/api";
import { MessageSquare, Check, ShieldCheck, Ban, HelpCircle, Repeat, Loader2 } from "lucide-react";

const TRANSACTIONAL_CONSENT_LANGUAGE =
  "I agree to receive order & account texts (delivery updates, receipts) from The Farm To You at the number provided. Msg & data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.";

const MARKETING_CONSENT_LANGUAGE =
  "I agree to receive marketing & promotional texts (deals, new drops) from The Farm To You at the number provided. Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.";

const SAMPLES = [
  "The Farm To You: your order #A1B2C3 just shipped! UPS #1Z999 Track: https://thefarmtoyou.com/track Reply STOP to opt out.",
  "The Farm To You: your order #A1B2C3 was delivered. Enjoy your farm-fresh cuts! Reply STOP to opt out.",
  "The Farm To You: a new members-only drop goes live Friday 10am ET: https://thefarmtoyou.com/drops Reply STOP to opt out.",
];

function Card({ icon: Icon, title, children, testId }) {
  return (
    <div className="card-flat p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-[var(--ff-primary)]">
        <Icon size={18} />
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      <div className="mt-3 text-sm text-[var(--ff-text-secondary)] space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}

function OptInForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!consent && !marketingConsent) { setErr("Please check at least one consent box to sign up."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sms/opt-in", { name, phone, transactional_consent: consent, marketing_consent: marketingConsent, source: "sms-terms" });
      setDone(data.message || "You're on the list!");
      setName(""); setPhone(""); setConsent(false); setMarketingConsent(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not sign you up. Please check your number and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 card-flat p-6 sm:p-8 border-2 border-[var(--ff-primary)]/20" data-testid="sms-optin-form-card">
      <div className="flex items-center gap-2 text-[var(--ff-primary)]">
        <MessageSquare size={18} />
        <h2 className="font-display text-xl">Sign up for text updates</h2>
      </div>
      {done ? (
        <p className="mt-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="sms-optin-success">
          <Check size={16} /> {done}
        </p>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-4" data-testid="sms-optin-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              className="w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:border-[var(--ff-primary)]"
              data-testid="sms-optin-name"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile number"
              required
              className="w-full rounded-lg border border-stone-300 px-4 py-3 text-sm focus:outline-none focus:border-[var(--ff-primary)]"
              data-testid="sms-optin-phone"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[var(--ff-primary)] shrink-0"
              data-testid="sms-optin-consent"
            />
            <span className="text-sm text-[var(--ff-text-secondary)]">{TRANSACTIONAL_CONSENT_LANGUAGE}</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[var(--ff-primary)] shrink-0"
              data-testid="sms-optin-marketing-consent"
            />
            <span className="text-sm text-[var(--ff-text-secondary)]">{MARKETING_CONSENT_LANGUAGE}</span>
          </label>
          {err && <p className="text-sm text-rose-600" data-testid="sms-optin-error">{err}</p>}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--ff-primary)] text-white px-6 py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="sms-optin-submit"
          >
            {busy ? <><Loader2 className="animate-spin" size={15} /> Signing up…</> : "Sign up for texts"}
          </button>
          <p className="text-xs text-[var(--ff-text-muted)]">
            By submitting, you agree to the terms above. You can reply STOP at any time to opt out.
          </p>
        </form>
      )}
    </div>
  );
}

export default function SmsConsent() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="sms-consent-page">
      <Seo
        title="SMS Terms & Messaging Consent · The Farm To You"
        description="How The Farm To You text messaging works: how to opt in, message types and frequency, how to get help, and how to opt out by replying STOP."
        canonical="/sms-terms"
      />

      <p className="overline text-[var(--ff-primary)]">Messaging Policy</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">SMS Terms &amp; Messaging Consent</h1>
      <p className="mt-4 text-base text-[var(--ff-text-secondary)] max-w-2xl">
        This page explains how text messaging from <strong>The Farm To You</strong> (THE FARM TO YOU LLC) works —
        how customers opt in, what messages we send, and how to get help or opt out at any time.
      </p>

      <OptInForm />

      {/* Program summary */}
      <div className="mt-8">
        <Card icon={MessageSquare} title="Program description" testId="sms-program">
          <p>
            The Farm To You runs <strong>two separate</strong> text programs, and you choose each one independently:
            a <strong>transactional</strong> program (order, shipping and account alerts) and a
            <strong> marketing</strong> program (deals, new drops and ambassador updates). Marketing consent is
            collected separately and is never required to make a purchase. Message frequency varies.
            <strong> Message &amp; data rates may apply.</strong>
          </p>
        </Card>
      </div>

      {/* Exact consent checkbox as shown to customers */}
      <div className="mt-6">
        <Card icon={Check} title="How customers opt in" testId="sms-optin">
          <p>
            Customers opt in on <strong>thefarmtoyou.com</strong> in three places — at <strong>checkout</strong>,
            during <strong>account sign-up</strong>, and on this <strong>SMS Terms page</strong> — by entering their
            mobile number and ticking one or both of two <strong>separate, unchecked</strong> consent boxes:
            one for order &amp; account texts and one for marketing texts. Each consent is optional and
            independent, and marketing consent is never required to make a purchase.
          </p>
          <p className="font-medium text-[var(--ff-text-primary)] mt-3">These are the two separate consent checkboxes shown to customers (each optional and unchecked by default):</p>
          <div className="mt-2 rounded-xl border border-stone-300 bg-stone-50 p-4 space-y-3" data-testid="sms-consent-checkbox-demo">
            <label className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border-2 border-stone-400 shrink-0" aria-hidden="true"></span>
              <span className="text-sm text-[var(--ff-text-secondary)]">{TRANSACTIONAL_CONSENT_LANGUAGE}</span>
            </label>
            <label className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border-2 border-stone-400 shrink-0" aria-hidden="true"></span>
              <span className="text-sm text-[var(--ff-text-secondary)]">{MARKETING_CONSENT_LANGUAGE}</span>
            </label>
          </div>
          <p className="text-xs text-[var(--ff-text-muted)] mt-2">
            Opt-in keywords: <strong>START</strong>, YES, UNSTOP. After opting in you'll receive:
            <span className="italic"> "The Farm To You: You're now opted in. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel."</span>
          </p>
        </Card>
      </div>

      {/* Sample messages */}
      <div className="mt-6">
        <Card icon={MessageSquare} title="Sample messages" testId="sms-samples">
          <ul className="space-y-2">
            {SAMPLES.map((s, i) => (
              <li key={i} className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-xs font-mono text-[var(--ff-text-secondary)]">{s}</li>
            ))}
          </ul>
        </Card>
      </div>

      {/* HELP / STOP / START */}
      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Card icon={HelpCircle} title="Get help" testId="sms-help">
          <p>Reply <strong>HELP</strong> or INFO at any time. You can also email <a href="mailto:support@thefarmtoyou.com" className="underline">support@thefarmtoyou.com</a>.</p>
          <p className="text-xs text-[var(--ff-text-muted)]">Reply: "Reply STOP to unsubscribe. Msg&amp;Data Rates May Apply."</p>
        </Card>
        <Card icon={Ban} title="Opt out anytime" testId="sms-stop">
          <p>Reply <strong>STOP</strong> (or CANCEL, QUIT, UNSUBSCRIBE, END) to stop all messages.</p>
          <p className="text-xs text-[var(--ff-text-muted)]">Reply: "You have successfully been unsubscribed. You will not receive any more messages from this number. Reply START to resubscribe."</p>
        </Card>
        <Card icon={Repeat} title="Resubscribe" testId="sms-start">
          <p>Changed your mind? Reply <strong>START</strong> to begin receiving messages again, or re-enable texts in Account → SMS Preferences.</p>
        </Card>
      </div>

      {/* Privacy */}
      <div className="mt-6">
        <Card icon={ShieldCheck} title="Your privacy" testId="sms-privacy">
          <p>
            Your consent and mobile information are <strong>never shared with or sold to third parties</strong> for
            their marketing purposes. Consent to receive texts is <strong>not a condition of any purchase</strong>.
          </p>
          <p>
            For full details, see our <a href="/privacy" className="underline">Privacy Policy</a> and the
            <a href="/terms#sms" className="underline"> SMS / Text Messaging Terms</a>.
          </p>
        </Card>
      </div>

      <p className="mt-8 text-xs text-[var(--ff-text-muted)]">
        The Farm To You · THE FARM TO YOU LLC · Questions? <a href="/contact" className="underline">Contact us</a> or email support@thefarmtoyou.com.
      </p>
    </div>
  );
}
